import React from "react";
import { Box, Typography, LinearProgress, Tooltip } from "@mui/material";

/**
 * Color based on usage percentage.
 */
function getColor(pct) {
  if (pct >= 90) return "error";
  if (pct >= 70) return "warning";
  return "success";
}

/**
 * ResourceGauge — Reusable progress bar with label and fraction text.
 *
 * Props:
 *  - label: string (e.g. "Sites", "Storage")
 *  - used: number
 *  - limit: number
 *  - unit: string (optional, e.g. "GB")
 *  - pct: number (0-100, computed if not provided)
 *  - compact: boolean (smaller variant)
 */
export default function ResourceGauge({
  label,
  used,
  limit,
  unit = "",
  pct,
  compact = false,
}) {
  const percentage = pct ?? (limit > 0 ? Math.round((used / limit) * 100) : 0);
  const clampedPct = Math.min(percentage, 100);
  const color = getColor(percentage);

  const fractionText = unit
    ? `${used} / ${limit} ${unit}`
    : `${used} / ${limit}`;

  if (compact) {
    return (
      <Box sx={{ mb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.25,
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fractionText}
          </Typography>
        </Box>
        <Tooltip title={`${percentage}%`} arrow>
          <LinearProgress
            variant="determinate"
            value={clampedPct}
            color={color}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {fractionText}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={clampedPct}
        color={color}
        sx={{ height: 8, borderRadius: 4 }}
      />
      <Typography
        variant="caption"
        color={`${color}.main`}
        sx={{ mt: 0.25, display: "block", textAlign: "right" }}
      >
        {percentage}%
      </Typography>
    </Box>
  );
}
